import { type FC, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';

import { rpc } from '…/app/common/rpc/client.mts';
import { Combobox } from '…/app/common/Combobox/Combobox.tsx';
import { Button } from '…/app/common/Button/Button.tsx';
import { useStore } from '…/app/state/useStore.mts';

import { Dialog } from '…/app/w/workspace/common/Dialog/Dialog.tsx';
import { createWorkspace } from '../queries/createWorkspace.op.mts';

interface CreateWorkspaceDialogProps {
  open: boolean;
  setOpen: (open: boolean) => void;
}

const workspaceTypeOptions = [
  {
    id: 'UNKNOWN',
    name: 'Unknown',
  },
  {
    id: 'CUSTOMER',
    name: 'Customer',
  },
  {
    id: 'PAID_PRIVATE',
    name: 'Paid/Private',
  },
  {
    id: 'VC_PORTFOLIO',
    name: 'VC Portfolio',
  },
  {
    id: 'OTHER',
    name: 'Other',
  },
];

const organizationTypeOptions = [
  {
    id: 'UNKNOWN',
    name: 'Unknown',
  },
  {
    id: 'EDUCATION',
    name: 'Education',
  },
  {
    id: 'NON_PROFIT',
    name: 'Non-Profit',
  },
  {
    id: 'ASSOCIATION',
    name: 'Association',
  },
  {
    id: 'COMMERCIAL',
    name: 'Commercial',
  },
];

const pricingOptions = [
  {
    id: 'HB_PU_401',
    name: 'HB PU 401 - Orbiit Essential',
  },
  {
    id: 'HB_PU_404',
    name: 'HB PU 404 - AI Matching add-on',
  },
  {
    id: 'ESSENTIAL',
    name: 'Essential (Legacy)',
  },
  {
    id: 'PRO',
    name: 'Pro (Legacy)',
  },
  {
    id: 'PREMIUM',
    name: 'Premium (Legacy)',
  },
];

const timezoneOptions = [
  {
    id: 'America/Los_Angeles',
    name: 'America/Los_Angeles',
  },
  {
    id: 'America/Denver',
    name: 'America/Denver',
  },
  {
    id: 'America/Chicago',
    name: 'America/Chicago',
  },
  {
    id: 'America/New_York',
    name: 'America/New_York',
  },
  {
    id: 'Europe/London',
    name: 'Europe/London',
  },
  {
    id: 'Europe/Paris',
    name: 'Europe/Paris',
  },
  {
    id: 'Asia/Jerusalem',
    name: 'Asia/Jerusalem',
  },
  {
    id: 'Asia/Singapore',
    name: 'Asia/Singapore',
  },
  {
    id: 'Australia/Sydney',
    name: 'Australia/Sydney',
  },
  {
    id: 'Australia/Melbourne',
    name: 'Australia/Melbourne',
  },
];

// Define schema for form validation
const formSchema = z.object({
  name: z.string().min(1, 'Workspace name is required'),
  organizationType: z.object({
    id: z.string(),
    name: z.string(),
  }).nullable(),
  pricingPlan: z.object({
    id: z.string(),
    name: z.string(),
  }).nullable(),
  timezone: z.object({
    id: z.string(),
    name: z.string(),
  }).nullable(),
  workspaceType: z.object({
    id: z.string(),
    name: z.string(),
  }).nullable(),
});

type FormValues = z.infer<typeof formSchema>;

export const CreateWorkspaceDialog: FC<CreateWorkspaceDialogProps> = ({
  open,
  setOpen,
}) => {
  const { emails } = useStore((data) => data.user);

  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [success, setSuccess] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const enableLegacyPricingPlans = emails.some((email: string) => ['luuk@orbiit.ai', 'luuk.derksen@hivebrite.com'].includes(email));

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<FormValues>({
    defaultValues: {
      name: '',
      organizationType: organizationTypeOptions[0],
      pricingPlan: pricingOptions[0],
      timezone: timezoneOptions[3],
      workspaceType: workspaceTypeOptions[0],
    },
    resolver: zodResolver(formSchema),
  });

  const onSubmit = async (data: FormValues) => {
    setIsSaving(true);
    try {
      const args = {
        name: data.name,
        organizationType: data.organizationType.id,
        pricingPlan: data.pricingPlan.id,
        timezone: data.timezone.id,
        workspaceType: data.workspaceType.id,
      };
      const workspaceId = await createWorkspace(args);
      setSuccess(JSON.stringify({
        id: workspaceId,
        ...args,
      }, null, 4));
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      await rpc.post('proxy/refresh-tokens', {
        json: { orb: true },
      });

      setIsSaving(false);
    }
  };

  const onClose = () => {
    setSuccess(null);
    setIsSaving(false);
    setError(null);
    setOpen(false);

    if (window) window.location.reload();
  };

  return (
    <Dialog
      backdropped
      open={!!open}
      setOpen={onClose}
    >
      {!success ? (
        <form
          className="w-full p-4 space-y-6"
          onSubmit={handleSubmit(onSubmit)}
        >
          <h2 className="text-xl font-medium text-gray-800">
            Create New Workspace
          </h2>

          <div className="space-y-2">
            <label
              className="block text-sm font-medium text-gray-700"
              htmlFor="name"
            >
              Workspace Name
            </label>

            <input
              className={`w-full px-3 py-3 border border-gray-400 border-solid rounded-md appearance-none outline-none focus:outline-none focus:border-blue-500 ${
                errors.name ? 'border-red-500' : ''
              }`}
              id="name"
              placeholder="Enter workspace name"
              type="text"
              {...register('name')}
            />

            {errors.name && (
              <p className="text-red-500 text-sm">{errors.name.message}</p>
            )}
          </div>

          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">
              Select an Organization Type
            </label>

            <Controller
              control={control}
              name="organizationType"
              render={({ field }) => {
                // Map field.value from { id, name } to { value, label }
                const formattedValue = field.value
                  ? {
                    label: field.value.name,
                    value: field.value.id,
                  }
                  : null;

                // Map options to format expected by react-select
                const formattedOptions = organizationTypeOptions.map(option => ({
                  label: option.name,
                  value: option.id,
                }));

                // Handle onChange to transform back to original format
                const handleChange = (selectedOption: {
                  label: string;
                  value: string;
                } | null) => {
                  if (selectedOption) {
                    const original = organizationTypeOptions.find(option => option.id === selectedOption.value);
                    field.onChange(original);
                  } else {
                    field.onChange(null);
                  }
                };

                return (
                  <Combobox
                    name="organizationType"
                    onChange={handleChange}
                    options={formattedOptions}
                    placeholder="Select an organization type"
                    value={formattedValue}
                  />
                );
              }}
            />

            {errors.organizationType && (
              <p className="text-red-500 text-sm">{errors.organizationType.message}</p>
            )}
          </div>

          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">
              Select a Customer Type
            </label>

            <Controller
              control={control}
              name="workspaceType"
              render={({ field }) => {
                // Map field.value from { id, name } to { value, label }
                const formattedValue = field.value
                  ? {
                    label: field.value.name,
                    value: field.value.id,
                  }
                  : null;

                // Map options to format expected by react-select
                const formattedOptions = workspaceTypeOptions.map(option => ({
                  label: option.name,
                  value: option.id,
                }));

                // Handle onChange to transform back to original format
                const handleChange = (selectedOption: {
                  label: string;
                  value: string;
                } | null) => {
                  if (selectedOption) {
                    const original = workspaceTypeOptions.find(option => option.id === selectedOption.value);
                    field.onChange(original);
                  } else {
                    field.onChange(null);
                  }
                };

                return (
                  <Combobox
                    name="workspaceType"
                    onChange={handleChange}
                    options={formattedOptions}
                    placeholder="Select a customer type"
                    value={formattedValue}
                  />
                );
              }}
            />

            {errors.workspaceType && (
              <p className="text-red-500 text-sm">{errors.workspaceType.message}</p>
            )}
          </div>

          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">
              Select a Pricing Plan
            </label>

            <Controller
              control={control}
              name="pricingPlan"
              render={({ field }) => {
                // Map field.value from { id, name } to { value, label }
                const formattedValue = field.value
                  ? {
                    label: field.value.name,
                    value: field.value.id,
                  }
                  : null;

                // Map options to format expected by react-select
                const formattedOptions = pricingOptions.filter(option => option.id.includes('HB_PU') || enableLegacyPricingPlans).map(option => ({
                  label: option.name,
                  value: option.id,
                }));

                // Handle onChange to transform back to original format
                const handleChange = (selectedOption: {
                  label: string;
                  value: string;
                } | null) => {
                  if (selectedOption) {
                    const original = pricingOptions.find(option => option.id === selectedOption.value);
                    field.onChange(original);
                  } else {
                    field.onChange(null);
                  }
                };

                return (
                  <Combobox
                    name="pricingPlan"
                    onChange={handleChange}
                    options={formattedOptions}
                    placeholder="Select a pricing plan"
                    value={formattedValue}
                  />
                );
              }}
            />

            {errors.pricingPlan && (
              <p className="text-red-500 text-sm">{errors.pricingPlan.message}</p>
            )}
          </div>

          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">
              Select a Timezone
            </label>

            <Controller
              control={control}
              name="timezone"
              render={({ field }) => {
                const formattedValue = field.value
                  ? {
                    label: field.value.name,
                    value: field.value.id,
                  }
                  : null;

                const formattedOptions = timezoneOptions.map(option => ({
                  label: option.name,
                  value: option.id,
                }));

                const handleChange = (selectedOption: {
                  label: string;
                  value: string;
                } | null) => {
                  if (selectedOption) {
                    const original = timezoneOptions.find(option => option.id === selectedOption.value);
                    field.onChange(original);
                  } else {
                    field.onChange(null);
                  }
                };

                return (
                  <Combobox
                    name="timezone"
                    onChange={handleChange}
                    options={formattedOptions}
                    placeholder="Select a timezone"
                    value={formattedValue}
                  />
                );
              }}
            />

            {errors.timezone && (
              <p className="text-red-500 text-sm">{errors.timezone.message}</p>
            )}
          </div>

          {error && (
            <div className="text-red-500 text-sm py-2">
              {error}
            </div>
          )}

          <Button
            className="w-full"
            loading={isSaving}
            size="lg"
            type="submit"
            variant="secondary"
          >
            Create Workspace
          </Button>
        </form>
      ) : (
        <div className="space-y-4">
          <h2 className="text-xl font-medium text-gray-800">Workspace Created Successfully</h2>

          <p className="text-gray-600">Your new workspace has been created.</p>

          <div>
            <pre className="whitespace-pre-wrap bg-gray-100 text-gray-700 font-mono p-4 rounded-md text-sm">
              {success}
            </pre>
          </div>

          <div className="flex space-x-4">
            <Button
              onClick={onClose}
              variant="default"
            >
              Close
            </Button>
          </div>
        </div>
      )}
    </Dialog>
  );
};

CreateWorkspaceDialog.displayName = 'CreateWorkspaceDialog';
